import React, { FunctionComponent, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Box, Checkbox, Divider, FormControlLabel } from "@mui/material";
import dayjs from "dayjs";

import { QUERY_KEYS } from "consts";
import { CommentDTO, markCommentAsDone, markSiteCommentAsDone } from "api";
import { Body2Regular, COLORS, Label12Regular, Label14SemiBold, CommentTextField, SelectedUser } from "components";

import { ReplyControls } from "./components";

export const CommonComment: FunctionComponent<{
  comment: CommentDTO;
  isReply?: boolean;
  currentUserId: number;
  siteId?: any;
  estimateId?: any;
  onSuccessfulSiteSubmit?: () => void;
}> = ({ comment, isReply = false, currentUserId, siteId, estimateId, onSuccessfulSiteSubmit }) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(estimateId ? markSiteCommentAsDone : markCommentAsDone);

  const [isReplying, setIsReplying] = useState<boolean>(false);
  const [shouldShowReplies, setShouldShowReplies] = useState<boolean>(false);
  const isSystemComment = comment.authorName === "System" && comment.authorLastName === "comment";
  const isTask = !!comment.assignedUser;
  const isAssignedUser = currentUserId === comment.assignedUser?.id;

  return (
    <>
      <Box sx={{ backgroundColor: "#fff", p: 3, borderRadius: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Label14SemiBold sx={{ color: COLORS.black }}>
              {comment.authorName} {comment.authorLastName}
            </Label14SemiBold>
            <Body2Regular>{dayjs(comment.createDate).utc().local().format("MMM D [at] hh:mm a")}</Body2Regular>
          </Box>
          <Body2Regular>{comment.authorEmail}</Body2Regular>
        </Box>
        <Label12Regular sx={{ mt: 2, color: COLORS.black }}>
          <div
            // eslint-disable-next-line
            dangerouslySetInnerHTML={{
              __html: `<div>${comment.text.replace(/\\n/g, "<br>")}</div>`,
            }}
            style={{ whiteSpace: "pre-line" }}
          />
        </Label12Regular>
        {!isSystemComment && !isReply && !isTask &&
          <ReplyControls
            comment={comment}
            isReplying={isReplying}
            setIsReplying={setIsReplying}
            shouldShowReplies={shouldShowReplies}
            setShouldShowReplies={setShouldShowReplies}
          />}
        {isTask && (
          <>
            <Divider sx={{ mt: 2, mb: 1 }} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <SelectedUser user={comment.assignedUser} />
              </Box>
              {!isAssignedUser && (comment.done ? "Resolved" : "Not resolved")}
              {isAssignedUser && (
                <FormControlLabel
                  control={
                    <Checkbox
                      defaultChecked={comment.done}
                      disabled={comment.done || isLoading}
                      onChange={() =>
                        mutateAsync(comment.id, {
                          onSuccess: () => {
                            if (onSuccessfulSiteSubmit) {
                              onSuccessfulSiteSubmit();
                            }
                            queryClient.invalidateQueries([
                              QUERY_KEYS.COMMENTS,
                            ])
                          }
                        })
                      }
                    />
                  }
                  label="Resolve"
                />
              )}
              {!isSystemComment && !isReply &&
                <ReplyControls
                  comment={comment}
                  isReplying={isReplying}
                  setIsReplying={setIsReplying}
                  shouldShowReplies={shouldShowReplies}
                  setShouldShowReplies={setShouldShowReplies}
                />}
            </Box>
          </>
        )}
        {isReplying && !isSystemComment && !isReply && (
          <Box sx={{ mt: 1 }}>
            <CommentTextField
              id={estimateId}
              siteId={siteId}
              comment={comment}
              isEstimate={!!estimateId}
              onSuccessfulSubmit={() => {
                setIsReplying(false);
                setShouldShowReplies(true);
                if (onSuccessfulSiteSubmit) {
                  onSuccessfulSiteSubmit();
                }
              }}
            />
          </Box>
        )}
      </Box>

      {shouldShowReplies && !isSystemComment && !isReply && comment.replies.map((reply) => (
        <Box sx={{ mt: 2, ml: 8 }} key={reply.id}>
          <CommonComment comment={reply} isReply currentUserId={currentUserId} />
        </Box>
      ))}
    </>
  );
};
